import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { CategoryConfig, DateRangePreset, DateRangePresetCategory } from '../types';
import { getPresetLabelKey, isPresetInCategory, PRESET_CATEGORIES } from '../utils';

interface PresetCategoryItemProps {
  /** Category configuration */
  categoryConfig: CategoryConfig;
  /** Currently selected preset */
  selectedPreset: DateRangePreset | null;
  /** Callback when a preset is selected */
  onSelect: (preset: DateRangePreset) => void;
  /** Keep the dropdown open after selecting a preset (used inside filter submenus) */
  keepMenuOpenOnSelect?: boolean;
}

/**
 * A category item in the date range picker dropdown.
 * Shows a submenu with preset options (e.g., "this week", "last week", "next week").
 *
 * When a preset from this category is selected:
 * - The trigger shows the selected preset label (e.g. "Last month") in bold + underlined
 */
export function PresetCategoryItem({
  categoryConfig,
  selectedPreset,
  onSelect,
  keepMenuOpenOnSelect,
}: PresetCategoryItemProps) {
  const { t } = useTranslation();

  const { category, labelKey, presets } = categoryConfig;
  const hasSelectedPreset = isPresetInCategory(selectedPreset, category);

  const triggerLabel =
    hasSelectedPreset && selectedPreset ? t(getPresetLabelKey(selectedPreset)) : t(labelKey);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        className={cn(
          'flex w-full cursor-pointer items-center justify-between gap-2',
          hasSelectedPreset && 'font-bold underline'
        )}
      >
        <span>{triggerLabel}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-[160px]">
        {presets.map(({ preset, labelKey: presetLabelKey }) => {
          const isSelected = selectedPreset === preset;

          return (
            <DropdownMenuItem
              key={preset}
              onClick={() => onSelect(preset)}
              onSelect={keepMenuOpenOnSelect ? event => event.preventDefault() : undefined}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-2',
                isSelected && 'font-medium'
              )}
            >
              <span>{t(presetLabelKey)}</span>
              {isSelected && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

interface PresetCategoriesListProps {
  /** Currently selected preset */
  selectedPreset: DateRangePreset | null;
  /** Callback when a preset is selected */
  onSelect: (preset: DateRangePreset) => void;
  /** Categories to omit (e.g. Year/Quarter on the generate screens). */
  hiddenCategories?: DateRangePresetCategory[];
  /** Keep the dropdown open after selecting a preset (used inside filter submenus) */
  keepMenuOpenOnSelect?: boolean;
}

/**
 * Renders all preset category items (Day, Week, Month, Quarter, Year), minus any
 * listed in `hiddenCategories`.
 */
export function PresetCategoriesList({
  selectedPreset,
  onSelect,
  hiddenCategories,
  keepMenuOpenOnSelect,
}: PresetCategoriesListProps) {
  const categories = hiddenCategories?.length
    ? PRESET_CATEGORIES.filter(c => !hiddenCategories.includes(c.category))
    : PRESET_CATEGORIES;

  return (
    <>
      {categories.map(categoryConfig => (
        <PresetCategoryItem
          key={categoryConfig.category}
          categoryConfig={categoryConfig}
          selectedPreset={selectedPreset}
          onSelect={onSelect}
          keepMenuOpenOnSelect={keepMenuOpenOnSelect}
        />
      ))}
    </>
  );
}
